import { useState } from 'react';
import { router } from '@inertiajs/react';
import { ChevronLeft, ChevronRight, Lock, LockOpen, Pin } from 'lucide-react';
import { Button, StickyFooter } from '../ui';

/**
 * Bottom toolbar for a live session, used by both the teacher's session
 * page and the student's lobby. Pinned to the viewport bottom via
 * StickyFooter.
 *
 * Props:
 *  - role: "teacher" | "student". Controls which actions render.
 *  - peer: the OTHER party (student sees teacher; teacher sees student).
 *  - participants, status: shared informational pills.
 *  - joinCode, joinUrl, hasActivity, canActivate, onStep, onRefresh,
 *    onActivate: teacher-only.
 *  - The teacher exits via "Back to lobby", which navigates to the
 *    sessions index. Ending a session happens from there, not here.
 */
export default function LiveSessionController({
  role = 'teacher',
  status,
  peer,
  participants = [],
  joinCode,
  joinUrl,
  hasActivity,
  canActivate,
  onStep,
  onRefresh,
  onActivate,
  showReaderControls = false,
  readerLocked = true,
  onToggleReaderLock,
  pinnedPara = null,
  onPinHere,
  onPinStep,
}) {
  const [copied, setCopied] = useState(false);
  const isTeacher = role === 'teacher';

  const copyUrl = async () => {
    if (!joinUrl) return;
    await navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const connectedCount = participants.filter((p) => p.connected).length;

  return (
    <StickyFooter>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 w-full text-gray-900 dark:text-white">
        <PeerBadge peer={peer} role={role} />

        <StatusPill status={status} />

        <ParticipantsIndicator
          total={participants.length}
          connected={connectedCount}
        />

        {isTeacher && <CodeDisplay code={joinCode} />}

        {isTeacher && (
          <Button variant="ghost" size="sm" onClick={copyUrl} disabled={!joinUrl}>
            {copied ? 'Copied' : 'Copy URL'}
          </Button>
        )}

        <div className="flex-1" />

        {isTeacher && (
          <div className="flex items-center gap-2">
            {showReaderControls && (
              <>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Everyone sees
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onPinStep?.(-1)}
                  title="Pin the previous paragraph for everyone"
                >
                  <ChevronLeft size={14} />
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onPinHere}
                  title="Pin the paragraph at the top of your view for everyone"
                >
                  <Pin size={14} /> Pin here{pinnedPara != null ? ` · ¶${pinnedPara + 1}` : ''}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onPinStep?.(1)}
                  title="Pin the next paragraph for everyone"
                >
                  <ChevronRight size={14} />
                </Button>
                <Button
                  variant={readerLocked ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={onToggleReaderLock}
                  title={
                    readerLocked
                      ? 'The student is held at the pinned paragraph. Click to let them read freely.'
                      : 'The student can read freely. Click to hold them at the pin.'
                  }
                >
                  {readerLocked ? (
                    <><Lock size={14} /> Held</>
                  ) : (
                    <><LockOpen size={14} /> Free read</>
                  )}
                </Button>
              </>
            )}
            {!showReaderControls && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onStep?.('back')}
              disabled={!hasActivity}
            >
              ← Step back
            </Button>
            )}
            {!showReaderControls && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onStep?.('forward')}
              disabled={!hasActivity}
            >
              Step forward →
            </Button>
            )}
            {!showReaderControls && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 sidebar-live-glow"
              title="Pull the latest student state — useful if anything looks out of sync"
            >
              Refresh
            </Button>
            )}
            {canActivate && status !== 'active' && (
              <Button variant="primary" size="sm" onClick={onActivate}>
                Activate
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => router.visit('/teacher/live_sessions')}
            >
              Back to lobby
            </Button>
          </div>
        )}
      </div>
    </StickyFooter>
  );
}

function PeerBadge({ peer, role }) {
  if (!peer) {
    const placeholder =
      role === 'student' ? 'Waiting for teacher…' : 'No invited student';
    return (
      <span className="text-sm text-gray-500 dark:text-gray-400">{placeholder}</span>
    );
  }
  const subtitle =
    peer.connected
      ? 'Connected'
      : role === 'student'
      ? 'Teacher offline'
      : 'Waiting to join…';
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[11px] font-semibold text-gray-700 dark:text-gray-200">
        {peer.initials}
      </div>
      <div className="leading-tight">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {peer.full_name}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              peer.connected ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          />
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles =
    status === 'active'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
      : status === 'completed'
      ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${styles}`}
    >
      {status}
    </span>
  );
}

function ParticipantsIndicator({ total, connected }) {
  if (!total) return null;
  const allConnected = connected === total;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        In session
      </span>
      <span className="font-mono text-gray-900 dark:text-white">
        {connected}/{total}
      </span>
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          allConnected
            ? 'bg-green-500'
            : connected > 0
            ? 'bg-amber-400'
            : 'bg-gray-300 dark:bg-gray-600'
        }`}
        title={`${connected} of ${total} connected`}
      />
    </div>
  );
}

function CodeDisplay({ code }) {
  if (!code) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        Code
      </span>
      <span className="font-mono tracking-widest text-gray-900 dark:text-white">
        {code}
      </span>
    </div>
  );
}
